import Link from "next/link";
import { UserRound } from "lucide-react";

import { cn } from "@/lib/utils";
import { TUTORS, tutorXPct, tutorYPct, type Tutor } from "@/lib/tutors";

interface LocalTutorsMapProps {
  /** Defaults to all tutors. */
  tutors?: readonly Tutor[];
  /**
   * When set, pins whose href is not in this list are dimmed (used by the
   * one-to-one finder to spotlight the filtered matches). null/undefined = all
   * pins shown at full strength.
   */
  highlightHrefs?: readonly string[] | null;
  className?: string;
}

/**
 * Real, in-person AI tutors, mostly across the South West and South of England.
 * Shown as silhouette placeholders for now (a privacy placeholder while
 * availability is confirmed — no real photos); each pin links to that area's
 * local-training page.
 */
export function LocalTutorsMap({
  tutors = TUTORS,
  highlightHrefs = null,
  className,
}: LocalTutorsMapProps) {
  const highlightSet = highlightHrefs ? new Set(highlightHrefs) : null;

  // Pin positions, with overlapping pins nudged apart so each stays tappable.
  // Deterministic (no randomness), so it's safe to compute at render with no
  // hydration mismatch. The map is taller than wide, so vertical distances are
  // scaled by the aspect ratio when measuring overlap.
  const ASPECT = 1978 / 1280;
  const positions = (() => {
    const pts = tutors.map((t) => ({ x: tutorXPct(t.lng), y: tutorYPct(t.lat) }));
    const minGap = 7; // ~ pin diameter, in % of width
    for (let pass = 0; pass < 14; pass++) {
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[j].x - pts[i].x;
          const dyEq = (pts[j].y - pts[i].y) * ASPECT;
          const dist = Math.hypot(dx, dyEq) || 0.001;
          if (dist < minGap) {
            const push = (minGap - dist) / 2;
            const ux = dx / dist;
            const uy = dyEq / dist / ASPECT;
            pts[i].x -= ux * push;
            pts[j].x += ux * push;
            pts[i].y -= uy * push;
            pts[j].y += uy * push;
          }
        }
      }
    }
    return pts.map((p) => ({
      x: Math.max(4, Math.min(96, p.x)),
      y: Math.max(4, Math.min(97, p.y)),
    }));
  })();

  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-[420px] overflow-hidden rounded-md border border-line bg-light-bg shadow-sm",
        className,
      )}
      style={{ aspectRatio: "1280 / 1978" }}
    >
      {/* Real relief map underlay (Wikimedia, self-hosted). */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/uk-relief-map.jpg"
        alt="Relief map of the United Kingdom"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ filter: "saturate(0.82)" }}
      />
      {/* On-brand tint so the blurred faces read clearly on top. */}
      <div aria-hidden="true" className="absolute inset-0 bg-primary/15 mix-blend-multiply" />
      <div aria-hidden="true" className="absolute inset-0 bg-parchment/10" />

      {tutors.map((t, i) => {
        const dimmed = highlightSet ? !highlightSet.has(t.href) : false;
        return (
          <Link
            key={t.href}
            href={t.href}
            style={{ left: `${positions[i].x.toFixed(2)}%`, top: `${positions[i].y.toFixed(2)}%` }}
            className={cn(
              "group absolute z-10 -ml-5 -mt-5 flex size-10 items-center justify-center rounded-full hover:z-30 focus-within:z-30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
              dimmed
                ? "pointer-events-none z-0 opacity-25 saturate-0 transition-opacity"
                : "transition-opacity",
            )}
            aria-label={`AI training in ${t.town}, ${t.county}`}
          >
            {/* Silhouette placeholder — no real photos are shown. */}
            <span className="flex size-10 items-center justify-center rounded-full border-2 border-paper bg-primary/15 text-primary shadow-md ring-1 ring-primary/40 transition-transform group-hover:scale-110">
              <UserRound className="size-5" aria-hidden="true" />
            </span>
            <span className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border border-line bg-paper px-2 py-1 text-xs font-medium text-dark opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
              AI training in {t.town}
            </span>
          </Link>
        );
      })}

      {/* Licence attribution (CC BY-SA 3.0). */}
      <a
        href="https://commons.wikimedia.org/wiki/File:United_Kingdom_relief_location_map.jpg"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-1 right-1 z-10 rounded bg-paper/85 px-1.5 py-0.5 text-[9px] leading-none text-mid hover:text-primary"
      >
        Map: Alexrk2, CC BY-SA 3.0
      </a>
    </div>
  );
}
